import { Box, Flex } from '@chakra-ui/react';
import Actions, { IAction } from 'components/DataTable/Actions';
import { useConfirm } from 'contexts';
import { useToast } from 'hooks';
import { Icon, Text } from 'components/UI';
import { FaInfoCircle } from 'react-icons/fa';

export type CustomSelectParams<T extends object> = (cb: IAction<T>['callback']) => void;

export type BarProps<T extends object> = {
  selected: Record<string, T>;
  searchComponent?: React.ReactElement;
  filtering?: boolean;
  loading?: boolean;
  showViewInfo?: boolean;
  onFilter: () => void;
  actions: IAction<T>[];
  onCustomSelect: CustomSelectParams<T>;
};

const Bar = <T extends object>({
  selected,
  searchComponent: SearchComponent,
  filtering = false,
  loading = false,
  showViewInfo = false,
  onFilter,
  actions,
  onCustomSelect,
}: BarProps<T>) => {
  const [showToast] = useToast();
  const [showConfirm] = useConfirm();

  function handlePrompt(title: string, message: string, callback: CustomSelectParams<T>) {
    if (!selected || Object.keys(selected).length === 0) {
      showToast({
        message: 'No items selected.',
        status: 'warning',
      });

      return null;
    }

    return showConfirm({
      onConfirm: callback,
      title,
      message,
    });
  }

  const renderSearch = SearchComponent ? <Box>{SearchComponent}</Box> : null;

  const renderActions = actions ? (
    <Actions
      items={actions}
      filtering={filtering}
      loading={loading}
      callbacks={{
        bulkDelete(callback: IAction<T>['callback']) {
          handlePrompt('Bulk Delete', 'You are about to permanently delete these items, do you want to proceed?', () =>
            onCustomSelect(callback)
          );
        },
        bulkApprove: (callback: IAction<T>['callback']) =>
          handlePrompt('Bulk Approve', 'You are about to approve these items, do you want to proceed?', () =>
            onCustomSelect(callback)
          ),
        bulkDeny: (callback: IAction<T>['callback']) =>
          handlePrompt('Bulk Deny', 'You are about to deny these items, do you want to proceed?', () =>
            onCustomSelect(callback)
          ),
        customSelect: onCustomSelect,
        filter: onFilter,
      }}
    />
  ) : null;

  const renderViewInfo = showViewInfo ? (
    <Flex alignItems="center" pb={0.5}>
      <Icon as={FaInfoCircle} mr={2} color="gray.700" fontSize="md" />
      <Text fontSize="sm">Double click a row to view / edit.</Text>
    </Flex>
  ) : null;

  return (
    <Flex w="full" mb={3} justifyContent="space-between" alignItems="flex-end">
      {renderViewInfo}

      {renderSearch}
      {renderActions}
    </Flex>
  );
};

export default Bar;
